import React, { useState, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleLeft, faAngleRight, faEquals } from '@fortawesome/free-solid-svg-icons';
import NotLinkedDirsTable from './NotLinkedDirsTable';
import NotLinkedEmailsTable from './NotLinkedEmailsTable';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  flex: 1;
`;

const ContentArea = styled.div`
  display: flex;
  flex: 1;
  overflow: hidden;
  margin: 0 10px 10px 10px;
  padding: 0 10px 10px 10px;
`;

const LeftPanel = styled.div`
  width: ${props => props.width}%;
  overflow: ${props => props.width === 0 ? 'hidden' : 'auto'};
  padding-right: ${props => props.width === 0 ? '0' : '10px'};
  transition: width 0.3s ease;
  min-width: 0;

  tr.row-highlighted {
    background-color: #e3f2fd !important;

    &:hover {
      background-color: #bbdefb !important;
    }
  }

  #text-ellipsis-tooltip {
    z-index: 1000;
  }
`;

const RightPanel = styled.div`
  width: ${props => props.width}%;
  overflow: ${props => props.width === 0 ? 'hidden' : 'auto'};
  padding-left: ${props => props.width === 0 ? '0' : '10px'};
  transition: width 0.3s ease;
  min-width: 0;

  .not-linked-emails-container {
    display: flex;
    flex-direction: column;
    height: 100%;
  }

  .not-linked-emails-table-container {
    overflow: auto;
  }
`;

const Divider = styled.div`
  width: 6px;
  background-color: ${props => props.isHovered ? '#d0d0d0' : '#e0e0e0'};
  cursor: col-resize;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  flex-shrink: 0;
  transition: background-color 0.2s;
`;

const baseCollapseButtonStyles = `
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  background-color: #fff;
  border: 1px solid #ccc;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  opacity: ${props => props.isVisible ? 1 : 0};
  transition: opacity 0.2s ease;
  font-size: 12px;
  color: #666;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  z-index: 10;
`;

const CollapseRightButton = styled.div`
  ${baseCollapseButtonStyles}
  top: 10px;
`;

const CollapseLeftButton = styled.div`
  ${baseCollapseButtonStyles}
  top: 40px;
`;

const NotLinkedItemsView = ({
  notLinkedDirs,
  notLinkedEmails,
  nextMessageId,
  nextFilename,
  onLoadMoreEmails,
  onLoadMoreDirs,
  loaders
}) => {
  const [leftWidth, setLeftWidth] = useState(50); // percentage
  const [isDragging, setIsDragging] = useState(false);
  const [previousWidth, setPreviousWidth] = useState(50); // Store previous width for restore
  const [isDividerHovered, setIsDividerHovered] = useState(false);
  const containerRef = useRef(null);

  const handleMouseDown = (e) => {
    // Don't start dragging if clicking on the collapse button
    if (e.target.closest('.collapse-button')) {
      return;
    }
    setIsDragging(true);
    e.preventDefault();
  };

  const handleMouseMove = useCallback((e) => {
    if (!isDragging || !containerRef.current) return;

    const containerRect = containerRef.current.getBoundingClientRect();
    const newLeftWidth = ((e.clientX - containerRect.left) / containerRect.width) * 100;

    // Constrain between 0% and 100%
    if (newLeftWidth >= 20 && newLeftWidth <= 80) {
      setLeftWidth(newLeftWidth);
    }
  }, [isDragging]);

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const resetCollapse = () => {
    setLeftWidth(50);
    setPreviousWidth(50);
  };

  const collapseLeftPanel = () => {
    if (leftWidth > 0) {
      setPreviousWidth(leftWidth);
      setLeftWidth(0);
    } else {
      setLeftWidth(previousWidth);
    }
  };

  const collapseRightPanel = () => {
    if (leftWidth < 100) {
      setPreviousWidth(leftWidth);
      setLeftWidth(100);
    } else {
      setLeftWidth(previousWidth);
    }
  };

  // Add event listeners when dragging
  React.useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    } else {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isDragging, handleMouseMove]);

  return (
    <Container ref={containerRef}>
      <ContentArea>
        <LeftPanel width={leftWidth}>
          <NotLinkedDirsTable
            notLinkedDirs={notLinkedDirs}
            nextFilename={nextFilename}
            isLoadingMore={loaders && loaders.getNotLinkedDirs}
            onLoadMoreDirs={onLoadMoreDirs}
          />
        </LeftPanel>

        <Divider
          isHovered={isDividerHovered}
          onMouseDown={handleMouseDown}
          onMouseEnter={() => setIsDividerHovered(true)}
          onMouseLeave={() => setIsDividerHovered(false)}
        >
          <CollapseRightButton
            className="collapse-button"
            isVisible={isDividerHovered}
            onClick={leftWidth < 100 ? collapseRightPanel : resetCollapse}
            title={leftWidth < 100 ? "Hide right panel" : "Reset panels"}
          >
            <FontAwesomeIcon
              icon={leftWidth < 100 ? faAngleRight : faEquals}
            />
          </CollapseRightButton>

          <CollapseLeftButton
            className="collapse-button"
            isVisible={isDividerHovered}
            onClick={leftWidth > 0 ? collapseLeftPanel : resetCollapse}
            title={leftWidth > 0 ? "Hide left panel" : "Reset panels"}
          >
            <FontAwesomeIcon
              icon={leftWidth > 0 ? faAngleLeft : faEquals}
            />
          </CollapseLeftButton>
        </Divider>

        <RightPanel width={100 - leftWidth}>
          <NotLinkedEmailsTable
            notLinkedEmails={notLinkedEmails}
            nextMessageId={nextMessageId}
            isLoadingMore={loaders && loaders.loadMoreEmails}
            onLoadMoreEmails={onLoadMoreEmails}
          />
        </RightPanel>
      </ContentArea>
    </Container>
  );
};

NotLinkedItemsView.propTypes = {
  notLinkedDirs: PropTypes.array,
  notLinkedEmails: PropTypes.array,
  nextFilename: PropTypes.string,
  nextMessageId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onLoadMoreEmails: PropTypes.func,
  onLoadMoreDirs: PropTypes.func,
  loaders: PropTypes.object,
};

NotLinkedItemsView.defaultProps = {
  notLinkedDirs: [],
  notLinkedEmails: [],
  nextFilename: null,
  nextMessageId: null,
  onLoadMoreEmails: () => {},
  onLoadMoreDirs: () => {},
  loaders: {},
};

export default NotLinkedItemsView;